import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { ZoningType } from '@popety-io/types'
import {
  Map as MapComponent,
  createAnalysisBounds,
  createBoundingBoxFromCenter,
  type MapLayer,
  type VectorTileDynamicLayerData,
  type VTTooltipData,
  type VTPopoverData,
} from '../../organisms/Map'
import {
  TooltipContainer,
  TooltipOverlapHint,
  PopoverContainer,
  PopoverHeader,
  PopoverCloseButton,
  PopoverList,
  PopoverItem,
  PopoverScrollHint,
  ListingMapTooltip,
  TransactionMapTooltip,
  BuildingPermitMapTooltip,
} from '../MapTooltip'
import { MapPanels } from '../../organisms/Map/Map.panels'
import { useFullscreen } from '../../organisms/Map/hooks/core/useFullscreen'
import { useRouter } from '../../../hooks'
import { Icon } from '../../atoms/Icon'
import { AnalysisMapProps } from './AnalysisMap.types'
import {
  getMapStyleUrl,
  getMapStyleIdFromUrl,
  DEFAULT_MAP_STYLE_ID,
} from '../../../config/mapStylesConfig'
import { MapSectionProvider, useMapSection } from './context'
import { useAnalysisMapSectionDataOptional } from '../../organisms/analysis/contexts'
import {
  stopServesMode,
  type TransitMode,
} from '../../organisms/TransportAndAccessSection/transitStops'
import { useSectionLayers } from './hooks'
import { getSectionGroupTitle } from './config'
import type {
  LayerGroup,
  Layer,
} from '../../molecules/LayersPanel/LayersPanel.types'
import { SunlightLayerControls } from '../../molecules/LayersPanel/components'
import {
  amenityPOIsToGeoJSON,
  transportStopsToGeoJSON,
  isochronesToGeoJSON,
  evStationsToGeoJSON,
  highwayAccessToGeoJSON,
  roofDataToGeoJSON,
} from './utils/mapDataTransformers'
import { createSolarPotentialFillExpression } from './utils/energyLayerHelpers'
import { getVectorTilePopoverCopy } from './utils/vectorTilePopoverCopy'

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`

const FullscreenButton = styled.button<{ $isFullscreen?: boolean }>`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: none;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 5;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    top: 12px;
    left: 12px;
    width: 36px;
    height: 36px;
  }
`

// "Hide map" control — sits just to the right of the fullscreen button.
// Desktop only: below 1200px the map is a full-screen overlay with its own toggle.
const CollapseButton = styled.button`
  position: absolute;
  top: 16px;
  left: 64px;
  width: 40px;
  height: 40px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: none;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 5;

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 1200px) {
    display: none;
  }
`

// Default coordinates for Geneva, Switzerland if no coordinates provided
const DEFAULT_CENTER: [number, number] = [6.1424, 46.2022]
const PUBLIC_NAVIGATION_RADIUS_METERS = 2000

/**
 * Maps layer IDs to their translation keys in analysisMap.layers.*
 */
const LAYER_TRANSLATION_KEYS: Record<string, string> = {
  'listings-markers': 'listingsMarkers',
  'transaction-points': 'transactionPoints',
  'building-permits-markers': 'buildingPermitsMarkers',
  'amenities-shopping': 'amenitiesShopping',
  'amenities-grocery': 'amenitiesGrocery',
  'amenities-culture': 'amenitiesCulture',
  'amenities-dining': 'amenitiesDining',
  'amenities-education': 'amenitiesEducation',
  'amenities-wellness': 'amenitiesWellness',
  'amenities-healthcare': 'amenitiesHealthcare',
  'amenities-transport': 'amenitiesTransport',
  'amenities-services': 'amenitiesServices',
  'transport-bus-stops': 'transportBusStops',
  'transport-tram-stops': 'transportTramStops',
  'transport-train-stops': 'transportTrainStops',
  'transport-walking-isochrones': 'walkingTravelTime',
  'transport-cycling-isochrones': 'cyclingTravelTime',
  'transport-driving-isochrones': 'drivingTravelTime',
  'transport-ev-charging': 'transportEvCharging',
  'transport-highway-access': 'transportHighwayAccess',
  'demographics-isochrone': 'demographicsIsochrone',
  'demographics-population-density': 'demographicsPopulationDensity',
  'demographics-foreign-pct': 'demographicsForeignPct',
  'demographics-young-adult-pct': 'demographicsYoungAdultPct',
  'demographics-elderly-pct': 'demographicsElderlyPct',
  'demographics-growth-rate': 'demographicsGrowthRate',
  'demographics-age-group': 'demographicsAgeGroup',
  'demographics-household-type': 'demographicsHouseholdType',
  'demographics-residence-duration': 'demographicsResidenceDuration',
  'employment-isochrone': 'employmentIsochrone',
  'employment-density': 'employmentDensity',
  'employment-growth-rate': 'employmentGrowthRate',
  'employment-fulltime-ratio': 'employmentFulltimeRatio',
  'employment-jobs-per-establishment': 'employmentJobsPerEstablishment',
  'employment-dominant-sector': 'employmentDominantSector',
  'employment-public-private': 'employmentPublicPrivate',
  'purchase-market-apartment-price': 'purchaseMarketApartmentPrice',
  'purchase-market-house-price': 'purchaseMarketHousePrice',
  'rental-market-apartment-price': 'rentalMarketApartmentPrice',
  'rental-market-house-price': 'rentalMarketHousePrice',
  'noise-road-day': 'noiseRoadDay',
  'noise-road-night': 'noiseRoadNight',
  'noise-railway-day': 'noiseRailwayDay',
  'noise-railway-night': 'noiseRailwayNight',
  'restrictions-base': 'restrictionsBase',
  'hazards-base': 'hazardsBase',
  'zoning-all': 'zoningAll',
  'zoning-general-only': 'zoningGeneralOnly',
  'zoning-cantonal-only': 'zoningCantonalOnly',
  'zoning-local-only': 'zoningLocalOnly',
  'zoning-partial-only': 'zoningPartialOnly',
  'zoning-map-style': 'zoningMapStyle',
  'zoning-satellite-map-style': 'zoningSatelliteMapStyle',
  'sunlight-shadow-simulation': 'shadowSimulation',
  'sunlight-hours': 'sunlightHours',
  'sun-direction': 'sunDirection',
  'energy-solar-potential': 'solarPotential',
  'shadow-context-buildings': 'nearbyBuildings',
}

/**
 * Child of MapComponent — receives `map`, `loaded`, and `dimension` via clone props.
 * Fires `onMapState` once the map instance is ready and on every dimension change.
 */
const MapStateForwarder: React.FC<{
  map?: mapboxgl.Map
  loaded?: boolean
  dimension?: '2D' | '3D'
  onMapState?: (state: { map: mapboxgl.Map; is3D: boolean }) => void
}> = ({ map, loaded, dimension, onMapState }) => {
  React.useEffect(() => {
    if (map && loaded) onMapState?.({ map, is3D: dimension === '3D' })
    // onMapState identity is the consumer's concern; deps cover instance + dimension changes
  }, [map, loaded, dimension])
  return null
}

/**
 * Internal component with map section context access
 */
const AnalysisMapInternal: React.FC<AnalysisMapProps> = ({
  isPublic = false,
  propertyId,
  coordinates = DEFAULT_CENTER,
  analysisType = 'land',
  onResizeStart,
  children,
  className,
  mapHeight = '100%',
  uiConfig,
  customLayers,
  onLayerToggle: _onLayerToggle,
  geoJson,
  distanceLimitValue,
  buildingGeoJson,
  buildingHeight,
  buildingDisplayHeight,
  buildingClassification,
  buildingProtectedRank,
  excludeBuildingIds,
  customRadius,
  autoFitBounds = true,
  activeSection,
  sectionData,
  hoveredItemId: propHoveredItemId,
  selectedItemId: propSelectedItemId,
  setSelectedItemId: propSetSelectedItemId,
  flyToCoordinates: propFlyToCoordinates,
  onItemClick: propOnItemClick,
  initialDrawingData,
  onDrawingComplete,
  onDrawingUpdate,
  onDrawingDelete,
  onMapState,
  onPointCloudToggle,
  showPointCloudControl = false,
  pointCloudActive: pointCloudActiveProp,
  onPointCloudButtonClick,
  overlayControls,
  onCollapseMap,
}) => {
  // Access map section context
  const {
    setActiveSection,
    setSectionData,
    sectionData: contextSectionData,
    hasActiveSectionLayers,
    activeSection: contextActiveSection,
    layerStates: dynamicLayerStates,
    toggleLayer: toggleDynamicLayer,
    setLayerOpacity,
    hoveredItemId: contextHoveredItemId,
    selectedItemId: contextSelectedItemId,
    flyToCoordinates: contextFlyToCoordinates,
    onItemClick: contextOnItemClick,
    selectedZoningType,
    setSelectedZoningType,
    disabledZoningTypes,
    isSatelliteZoning,
  }: any = useMapSection()

  const { t: tUi } = useTranslation('ui')

  // Read the UI-level analysis context (shared tabs push data here)
  const analysisMapContext = useAnalysisMapSectionDataOptional()

  // Use props if provided, otherwise fall back to context
  const hoveredItemId = propHoveredItemId ?? contextHoveredItemId
  const selectedItemId = propSelectedItemId ?? contextSelectedItemId
  const setSelectedItemId = propSetSelectedItemId ?? undefined
  const flyToCoordinates = propFlyToCoordinates ?? contextFlyToCoordinates
  const onItemClick = propOnItemClick ?? contextOnItemClick

  // Get dynamic layer configurations for the active section
  const sectionLayerConfigs = useSectionLayers()

  // Sync activeSection from props (needed for section navigation)
  useEffect(() => {
    setActiveSection(activeSection || null)
  }, [activeSection, setActiveSection])

  // Sync sectionData from props when it changes
  // Merge with existing data to preserve user-controlled values (like sunlight slider)
  useEffect(() => {
    if (sectionData) {
      setSectionData((prev: any) => ({
        ...prev,
        ...sectionData,
      }))
    }
  }, [sectionData, setSectionData])

  // Sync section data from the UI-level analysis context (shared tabs push data here)
  useEffect(() => {
    if (analysisMapContext?.sectionData) {
      setSectionData((prev: any) => ({
        ...prev,
        ...analysisMapContext.sectionData,
      }))
    }
  }, [analysisMapContext?.sectionData, setSectionData])

  // Sync activeSection from UI-level analysis context (shared tabs set this)
  useEffect(() => {
    if (analysisMapContext?.activeSection) {
      setActiveSection(analysisMapContext.activeSection)
    }
  }, [analysisMapContext?.activeSection, setActiveSection])

  // Sync zoningData specifically when it changes (needed for zoning section)
  useEffect(() => {
    if (sectionData?.zoningData) {
      setSectionData((prev: any) => ({
        ...prev,
        zoningData: sectionData.zoningData,
      }))
    }
  }, [sectionData?.zoningData, setSectionData])

  // Auto-select historical imagery layer when a history event is clicked
  useEffect(() => {
    // Only run for history section with a selected year
    if (
      contextActiveSection === 'history' &&
      sectionData?.selectedYear &&
      typeof sectionData.selectedYear === 'string'
    ) {
      const yearToSelect = sectionData.selectedYear
      const layerIdToSelect = `historical-imagery-${yearToSelect}`

      // Find all historical imagery layers
      const historicalImageryLayers = sectionLayerConfigs.filter((config) =>
        config.id.startsWith('historical-imagery-')
      )

      // Disable all historical imagery layers except the selected one
      historicalImageryLayers.forEach((layerConfig) => {
        const shouldEnable = layerConfig.id === layerIdToSelect
        toggleDynamicLayer(layerConfig.id, shouldEnable)
      })
    }
  }, [
    contextActiveSection,
    sectionData?.selectedYear,
    sectionLayerConfigs,
    toggleDynamicLayer,
  ])

  // Handle fly to coordinates when a listing is selected
  const [flyToPosition, setFlyToPosition] = useState<
    [number, number] | undefined
  >(undefined)

  useEffect(() => {
    if (flyToCoordinates) {
      // Handle both formats: [lng, lat] or { coordinates: [lng, lat], zoom?: number }
      let coords: [number, number] | undefined
      if (Array.isArray(flyToCoordinates)) {
        coords = flyToCoordinates as [number, number]
      } else if ((flyToCoordinates as any).coordinates) {
        coords = (flyToCoordinates as any).coordinates
      }
      if (coords && Number.isFinite(coords[0]) && Number.isFinite(coords[1])) {
        setFlyToPosition([coords[0], coords[1]])
      }
    }
  }, [flyToCoordinates])

  // Handle dynamic layer click (e.g., marker click)
  const handleDynamicLayerClick = useCallback(
    (feature: any, layerId: string) => {
      // When a listing marker is clicked, find the full listing object and call the handler
      if (layerId === 'listings-markers' && onItemClick && sectionData) {
        const listings = (sectionData as any).listings || []
        const listingId = feature.id
        const listing = listings.find((l: any) => l.id === listingId)

        if (listing) {
          onItemClick(listing)
        }
      }

      // When a building permit marker is clicked, find the full permit object and call the handler
      if (
        layerId === 'building-permits-markers' &&
        onItemClick &&
        sectionData
      ) {
        const permits = (sectionData as any).building_permits || []
        const permitId = feature.id
        const permit = permits.find((p: any) => p.id === permitId)

        if (permit) {
          onItemClick(permit)
        }
      }

      // When a transaction marker is clicked, find the full transaction object and call the handler
      if (layerId === 'transaction-points' && onItemClick && sectionData) {
        const transactions = (sectionData as any).transactions || []
        const transactionId = feature.id
        const transaction = transactions.find(
          (t: any) => t.id === transactionId
        )

        if (transaction) {
          onItemClick(transaction)
        }
      }
    },
    [onItemClick, sectionData]
  )

  // Load persisted map state from localStorage
  const persistedMapState = useMemo(() => {
    try {
      const stored = localStorage.getItem(`map-state-analysis-${analysisType}`)
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  }, [analysisType])

  // URL is the shareable source of truth for basemap + point-cloud (like dimension, which Map.tsx
  // already round-trips). Analysis defaults: point cloud OFF (opt-in) → `?pointcloud=1` means ON;
  // basemap street → `?basemap=satellite` means satellite. (Explore uses the inverse pointcloud
  // default — default ON, `pointcloud=0` for off — because Explore shows the cloud by default.)
  const { query, updateQuery } = useRouter()

  // State for point cloud control button — seeded from the URL so a shared link restores it.
  const [pointCloudActive, setPointCloudActive] = useState(
    () => query.pointcloud === '1'
  )

  // When a consumer supplies onPointCloudButtonClick the button becomes a controlled popover
  // trigger: the consumer owns the active highlight + URL seeding, so the internal toggle/effect
  // step aside (keeps Building Analysis + Explore on the existing uncontrolled path).
  const controlled = onPointCloudButtonClick !== undefined
  const pointCloudButtonActive = controlled
    ? !!pointCloudActiveProp
    : pointCloudActive

  // Propagate the URL-seeded point-cloud state to the consumer (page mounts the overlay) once, so a
  // `?pointcloud=1` deep link actually loads the cloud (the page's enabled flag mirrors this toggle).
  useEffect(() => {
    if (!controlled && pointCloudActive) onPointCloudToggle?.(true)
  }, [])

  // State for layers panel
  const [layersPanelOpen, setLayersPanelOpen] = useState(false)
  const [legendPanelOpen, setLegendPanelOpen] = useState(
    () => persistedMapState?.panelStates?.legend ?? false
  )
  const [selectedThematicLayer, setSelectedThematicLayer] = useState('')
  const [currentMapStyle, setCurrentMapStyle] = useState<string>(() => {
    // URL basemap wins over the persisted style so a shared `?basemap=satellite` link is honoured.
    if (query.basemap === 'satellite') return 'mapbox-satellite'
    if (query.basemap === 'street') return DEFAULT_MAP_STYLE_ID
    if (persistedMapState?.mapStyle) {
      return getMapStyleIdFromUrl(persistedMapState.mapStyle)
    }
    return DEFAULT_MAP_STYLE_ID
  })

  // Track if a style change was user-initiated to prevent restoration callback from overriding it
  const userInitiatedStyleChangeRef = useRef(false)

  // VT tooltip/popover state for rich rendering
  const [vtTooltipData, setVtTooltipData] = useState<VTTooltipData | null>(null)
  const [vtPopoverData, setVtPopoverData] = useState<VTPopoverData | null>(null)

  // Fullscreen functionality
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const { isFullscreen, toggleFullscreen } = useFullscreen(
    mapContainerRef as any
  )

  // State for layer visibility
  const [layerStates, setLayerStates] = useState<Record<string, boolean>>({
    // Map Details layers
    streets: true,
    labels: true,
    terrain: false,
    contours: false,
    // Land Property layers
    'parcel-boundaries': true,
    'land-dimension-metrics': false,
    'distance-limit': true,
    // Building Property layers
    'building-footprints': true,
    'building-number-labels': false,
    'building-3d': false,
    'building-dimension-metrics': false,
  })

  // Legend configurations for isochrone layers
  const minuteLabels = useMemo(
    () => [
      tUi('analysisMap.legends.min', { count: 5 }),
      tUi('analysisMap.legends.min', { count: 10 }),
      tUi('analysisMap.legends.min', { count: 15 }),
      tUi('analysisMap.legends.min', { count: 30 }),
    ],
    [tUi]
  )

  const legendConfigs = useMemo(
    () => ({
      'transport-walking-isochrones': {
        type: 'gradient' as const,
        title: tUi('analysisMap.legends.walkingTravelTime'),
        labels: minuteLabels,
        unit: '',
      },
      'transport-cycling-isochrones': {
        type: 'gradient' as const,
        title: tUi('analysisMap.legends.cyclingTravelTime'),
        labels: minuteLabels,
        unit: '',
      },
      'transport-driving-isochrones': {
        type: 'gradient' as const,
        title: tUi('analysisMap.legends.drivingTravelTime'),
        labels: minuteLabels,
        unit: '',
      },
    }),
    [tUi, minuteLabels]
  )

  // Auto-show/hide legend when isochrone layers are toggled
  useEffect(() => {
    const isochroneLayers = [
      'transport-walking-isochrones',
      'transport-cycling-isochrones',
      'transport-driving-isochrones',
    ]

    // Find which isochrone layer is enabled (only one can be enabled due to exclusive group)
    const enabledIsochrone = isochroneLayers.find(
      (layerId) => dynamicLayerStates[layerId]?.enabled
    )

    if (enabledIsochrone) {
      // Show legend with the enabled isochrone
      setSelectedThematicLayer(enabledIsochrone)
      setLegendPanelOpen(true)
    } else if (isochroneLayers.includes(selectedThematicLayer)) {
      // Hide legend if the previously selected isochrone is now disabled
      setSelectedThematicLayer('')
      setLegendPanelOpen(false)
    }
  }, [dynamicLayerStates, selectedThematicLayer])

  // Handle zoning map style switching
  const preZoningStyleRef = useRef<string | null>(null)
  const lastAppliedStyleRef = useRef<string | null>(null)
  const prevSectionRef = useRef<string | null>(null)

  // Effect 1: Track user changes to Map base (to update restoration target)
  useEffect(() => {
    const isZoningSection =
      contextActiveSection === 'zoning_data' ||
      contextActiveSection === 'zoning_rules'

    // If we're in zoning and the style changed to a non-zoning style,
    // it means the user manually changed Map base - save it for restoration
    if (
      isZoningSection &&
      !currentMapStyle.startsWith('popety-zoning') &&
      currentMapStyle !== lastAppliedStyleRef.current
    ) {
      preZoningStyleRef.current = currentMapStyle
    }
  }, [currentMapStyle, contextActiveSection])

  // Effect 2: Handle zoning section transitions (entering/leaving)
  useEffect(() => {
    const isZoningSection =
      contextActiveSection === 'zoning_data' ||
      contextActiveSection === 'zoning_rules'
    const wasZoningSection =
      prevSectionRef.current === 'zoning_data' ||
      prevSectionRef.current === 'zoning_rules'

    // Detect transition: ENTERING zoning section
    if (!wasZoningSection && isZoningSection) {
      // Save current style when entering zoning
      // If already on a zoning style, save the default style as fallback
      const styleToSave = currentMapStyle.startsWith('popety-zoning')
        ? DEFAULT_MAP_STYLE_ID
        : currentMapStyle

      preZoningStyleRef.current = styleToSave
    }

    // Detect transition: LEAVING zoning section
    if (wasZoningSection && !isZoningSection) {
      // Restore saved style when leaving zoning (with fallback to default)
      const styleToRestore = preZoningStyleRef.current || DEFAULT_MAP_STYLE_ID

      lastAppliedStyleRef.current = styleToRestore
      setCurrentMapStyle(styleToRestore)
      preZoningStyleRef.current = null
    }

    // Update previous section for next comparison
    prevSectionRef.current = contextActiveSection
  }, [contextActiveSection])

  // Effect 3: Apply zoning style WHILE in zoning section
  useEffect(() => {
    const isZoningSection =
      contextActiveSection === 'zoning_data' ||
      contextActiveSection === 'zoning_rules'

    if (!isZoningSection) return

    const satelliteZoningEnabled =
      dynamicLayerStates['zoning-satellite-map-style']?.enabled
    const targetStyle = satelliteZoningEnabled
      ? 'popety-zoning-satellite'
      : 'popety-zoning'

    // Apply the target style (check against ref to avoid reading state in effect)
    if (lastAppliedStyleRef.current !== targetStyle) {
      lastAppliedStyleRef.current = targetStyle
      setCurrentMapStyle(targetStyle)
    }
  }, [contextActiveSection, dynamicLayerStates])

  // Create appropriate bounding box for the analysis
  // Use useMemo to ensure bbox only recalculates when dependencies change
  const bbox = useMemo(() => {
    return createAnalysisBounds(
      coordinates,
      analysisType,
      customRadius,
      geoJson
    )
  }, [coordinates, analysisType, customRadius, geoJson])

  // Keep navigation limits separate from the tight initial property fit.
  const [longitude, latitude] = coordinates
  const publicMaxBbox = useMemo(
    () =>
      isPublic
        ? createBoundingBoxFromCenter(
            [longitude, latitude],
            PUBLIC_NAVIGATION_RADIUS_METERS
          )
        : undefined,
    [isPublic, longitude, latitude]
  )

  // Choose preset based on analysis type

  // Default UI configuration for analysis maps
  const defaultUiConfig = {
    showResizeHandle: true,
    showLayerSwitcher: true,
    showMapControls: true,
    showLayersButton: analysisType !== 'building', // Buildings might not need layers panel
    showLegendButton: analysisType === 'land', // Land analysis benefits from legend
    show3DButton: true,
    showPrintButton: analysisType === 'estimate',
    showZoomControls: true,
    showDrawingTools: true, // Enable drawing tools (including measurement) for all analysis maps
    showLocationButton: false, // Disable fly-to-location for analysis maps
    controlPositions: {
      layerSwitcher: 'top-left' as const,
      mapControls: 'top-right' as const,
      zoomControls: 'bottom-left' as const,
      drawingFAB: 'bottom-right' as const,
      locationFAB: 'bottom-right' as const,
    },
    ...uiConfig, // Allow overrides
  }

  const handleResizeStart = () => {
    onResizeStart?.()
  }

  // Handle layer toggle
  const handleLayerToggle = (layerId: string, enabled: boolean) => {
    setLayerStates((prev) => ({
      ...prev,
      [layerId]: enabled,
    }))

    // Call optional external handler
    _onLayerToggle?.(layerId, enabled)
  }

  // Handle map style change and dynamic layer radio selections from layer panel
  const handleMapStyleChange = (layerIdOrStyleId: string, value?: string) => {
    // If value is provided, this is a dynamic layer radio selection
    if (value) {
      handleLayerRadioSelect(layerIdOrStyleId, value)
    } else {
      // Otherwise, it's a map style change - track it for Effect 1
      userInitiatedStyleChangeRef.current = true // Mark as user-initiated
      lastAppliedStyleRef.current = layerIdOrStyleId
      setCurrentMapStyle(layerIdOrStyleId)
    }
  }

  // Legacy handler for backward compatibility (toggles between standard and satellite)
  const handleChangeMapStyle = () => {
    const next =
      currentMapStyle === 'popetyio-default'
        ? 'mapbox-satellite'
        : 'popetyio-default'
    setCurrentMapStyle(next)
    // Reflect the basemap in the URL: satellite → ?basemap=satellite, street → drop the param.
    updateQuery({
      basemap: next === 'mapbox-satellite' ? 'satellite' : undefined,
    })
  }

  // Create default map layers based on analysis type
  const defaultMapLayers = useMemo<MapLayer[]>(() => {
    // Helper to get layer state
    const getLayerState = (layerId: string, defaultState: boolean): boolean => {
      return layerStates[layerId] !== undefined
        ? layerStates[layerId]
        : defaultState
    }

    // Detect if we're using a Mapbox Standard style
    const isStandardStyle = currentMapStyle.includes('standard')

    // Define Land Property layers
    const landPropertyLayers: MapLayer[] = [
      {
        id: 'parcel-boundaries',
        name: tUi('analysisMap.baseLayers.parcelBoundaries'),
        enabled: getLayerState('parcel-boundaries', true),
        visible: getLayerState('parcel-boundaries', true),
      },
      {
        id: 'land-dimension-metrics',
        name: tUi('analysisMap.baseLayers.dimensionMetrics'),
        enabled: getLayerState('land-dimension-metrics', false),
        visible: getLayerState('land-dimension-metrics', false),
      },
      {
        id: 'distance-limit',
        name: tUi('analysisMap.baseLayers.distanceLimit'),
        enabled: getLayerState('distance-limit', true),
        visible: getLayerState('distance-limit', true),
      },
    ]

    // Define Building Property layers
    const buildingPropertyLayers: MapLayer[] = [
      {
        id: 'building-footprints',
        name: tUi('analysisMap.baseLayers.footprint'),
        enabled: getLayerState('building-footprints', true),
        visible: getLayerState('building-footprints', true),
      },
      {
        id: 'building-number-labels',
        name: tUi('analysisMap.baseLayers.buildingNumbers'),
        enabled: getLayerState('building-number-labels', false),
        visible: getLayerState('building-number-labels', false),
      },
      {
        id: 'building-3d',
        name: tUi('analysisMap.baseLayers.building3d'),
        enabled: getLayerState('building-3d', false),
        visible: getLayerState('building-3d', false),
      },
      {
        id: 'building-dimension-metrics',
        name: tUi('analysisMap.baseLayers.dimensionMetrics'),
        enabled: getLayerState('building-dimension-metrics', false),
        visible: getLayerState('building-dimension-metrics', false),
      },
    ]

    // Define Map Details layers
    const mapDetailsLayers: MapLayer[] = [
      {
        id: 'streets',
        name: tUi('analysisMap.baseLayers.streets'),
        enabled: getLayerState('streets', true),
        visible: getLayerState('streets', true),
        disabled: isStandardStyle, // Streets cannot be toggled in Standard styles
        disabledReason: isStandardStyle
          ? 'Street visibility cannot be controlled in Standard map styles. Streets are always part of the base map.'
          : undefined,
      },
      {
        id: 'labels',
        name: tUi('analysisMap.baseLayers.labels'),
        enabled: getLayerState('labels', true),
        visible: getLayerState('labels', true),
      },
      {
        id: 'terrain',
        name: tUi('analysisMap.baseLayers.terrain'),
        enabled: getLayerState('terrain', false),
        visible: getLayerState('terrain', false),
      },
      {
        id: 'contours',
        name: tUi('analysisMap.baseLayers.contours'),
        enabled: getLayerState('contours', false),
        visible: getLayerState('contours', false),
      },
    ]

    // Combine all layers
    const allLayers = [
      ...landPropertyLayers,
      ...buildingPropertyLayers,
      ...mapDetailsLayers,
    ]

    // Return custom layers if provided, otherwise return all layers
    return customLayers || allLayers
  }, [layerStates, currentMapStyle, customLayers, tUi])

  /**
   * Build dynamic layer groups for the layer panel based on active section
   */
  const dynamicLayerGroups = useMemo<LayerGroup[]>(() => {
    if (!contextActiveSection || !hasActiveSectionLayers()) {
      return []
    }

    const groupTitleTranslationKey: Record<string, string> = {
      'Purchase Listings': 'purchaseListings',
      'Rental Listings': 'rentalListings',
      Transactions: 'transactions',
      Amenities: 'amenities',
      'Transport & Access': 'transportAccess',
      Zoning: 'zoning',
      'Zoning Rules': 'zoningRules',
      Restrictions: 'restrictions',
      'Natural Hazards': 'naturalHazards',
      Demographics: 'demographics',
      Employment: 'employment',
      'Purchase Market': 'purchaseMarket',
      'Rental Market': 'rentalMarket',
      'Noise Analysis': 'noiseAnalysis',
      'Historical Satellite Images': 'historicalSatelliteImages',
      'Building Permits': 'buildingPermits',
      'Sunlight Study': 'sunlightStudy',
      Energy: 'energy',
    }
    const rawGroupTitle =
      getSectionGroupTitle(contextActiveSection) || 'Dynamic Layers'
    const groupTitleKey = groupTitleTranslationKey[rawGroupTitle]
    const groupTitle = groupTitleKey
      ? tUi(`analysisMap.groupTitles.${groupTitleKey}`)
      : tUi('analysisMap.groupTitles.dynamicLayers')

    // Special handling for restrictions section - create layers dynamically from data
    if (contextActiveSection === 'restrictions' && sectionData?.restrictions) {
      const restrictions = sectionData.restrictions as any[]

      // Get unique RDPPF types from the data
      const rdppfTypesMap = new Map<string, { id: string; name: string }>()
      restrictions.forEach((r) => {
        if (r.rdppfTypeId && r.type) {
          rdppfTypesMap.set(r.rdppfTypeId, {
            id: r.rdppfTypeId,
            name: r.type,
          })
        }
      })

      // Get shared opacity from first layer or use default (30%)
      const firstLayerId = `restrictions-${Array.from(rdppfTypesMap.keys())[0]}`
      const sharedOpacity = dynamicLayerStates[firstLayerId]?.opacity ?? 30

      // Create independent layer toggles for each RDPPF type
      const restrictionLayers: Layer[] = Array.from(rdppfTypesMap.values()).map(
        (rdppfType) => {
          const layerId = `restrictions-${rdppfType.id}`
          const layerState = dynamicLayerStates[layerId] || {
            enabled: true,
            opacity: sharedOpacity,
          }

          return {
            id: layerId,
            label: rdppfType.name,
            enabled: layerState.enabled,
            type: 'checkbox' as const,
            opacity: sharedOpacity, // Use shared opacity
            showOpacity: false, // Don't show individual opacity controls
          }
        }
      )

      // Return a single group with all restriction layers
      if (restrictionLayers.length > 0) {
        return [
          {
            id: 'restrictions',
            title: groupTitle,
            layers: restrictionLayers,
            groupType: 'dynamic', // Mark as dynamic to enable quick actions
            groupRenderType: 'multiple-checkbox',
            showGroupOpacity: true, // Show group-level opacity slider
            groupOpacity: sharedOpacity, // Shared opacity value
          },
        ]
      }

      return []
    }

    // Special handling for hazards section - create layers dynamically from data
    if (contextActiveSection === 'hazards' && sectionData?.hazardZones) {
      const hazardZones = sectionData.hazardZones as any[]

      const typesMap = new Map<string, { id: string; name: string }>()
      hazardZones.forEach((z) => {
        const key = z.type || z.name
        if (key) {
          typesMap.set(key, { id: key, name: key })
        }
      })

      const firstLayerId = `hazards-${Array.from(typesMap.keys())[0]}`
      const sharedOpacity = dynamicLayerStates[firstLayerId]?.opacity ?? 30

      const hazardTypeLabels: Record<string, string> = {
        flood: tUi('analysisMap.hazardTypes.flood'),
        landslide: tUi('analysisMap.hazardTypes.landslide'),
        rockslide: tUi('analysisMap.hazardTypes.rockslide'),
        avalanche: tUi('analysisMap.hazardTypes.avalanche'),
      }

      const hazardLayers: Layer[] = Array.from(typesMap.values()).map(
        (type) => {
          const layerId = `hazards-${type.id}`
          const layerState = dynamicLayerStates[layerId] || {
            enabled: true,
            opacity: sharedOpacity,
          }

          return {
            id: layerId,
            label: hazardTypeLabels[type.name] || type.name,
            enabled: layerState.enabled,
            type: 'checkbox' as const,
            opacity: sharedOpacity,
            showOpacity: false,
          }
        }
      )

      if (hazardLayers.length > 0) {
        return [
          {
            id: 'hazards',
            title: groupTitle,
            layers: hazardLayers,
            groupType: 'dynamic',
            groupRenderType: 'multiple-checkbox',
            showGroupOpacity: true,
            groupOpacity: sharedOpacity,
          },
        ]
      }

      return []
    }

    // Group layers by exclusive group
    const exclusiveGroups = new Map<string, typeof sectionLayerConfigs>()
    const independentLayers: typeof sectionLayerConfigs = []

    sectionLayerConfigs.forEach((layerConfig) => {
      if (layerConfig.exclusiveGroup) {
        const group = exclusiveGroups.get(layerConfig.exclusiveGroup) || []
        group.push(layerConfig)
        exclusiveGroups.set(layerConfig.exclusiveGroup, group)
      } else {
        independentLayers.push(layerConfig)
      }
    })

    const layerGroups: LayerGroup[] = []

    // Add exclusive groups as separate LayerGroups with exclusive-dropdown render type
    exclusiveGroups.forEach((groupLayers, groupId) => {
      // Determine if this group should show "No layer" option
      const shouldShowNoLayerOption =
        groupId === 'demographics-choropleth' ||
        groupId === 'employment-choropleth' ||
        groupId === 'transport-isochrones' ||
        groupId === 'purchase-market-choropleth' ||
        groupId === 'rental-market-choropleth' ||
        groupId === 'noise-choropleth' ||
        groupId === 'zoning-layers' ||
        groupId === 'historical-imagery'

      // Find which layer is currently enabled
      const enabledLayer = groupLayers.find((lc) => {
        const state = dynamicLayerStates[lc.id]
        // If state exists, use its enabled value; otherwise fall back to defaultEnabled
        const isEnabled =
          state !== undefined ? state.enabled : lc.defaultEnabled
        return isEnabled
      })

      // Determine selected value:
      // - If no layer is enabled and group supports "No layer", use 'none'
      // - Otherwise use enabled layer or default to first layer
      const selectedLayerId =
        !enabledLayer && shouldShowNoLayerOption
          ? 'none'
          : enabledLayer?.id || groupLayers[0].id

      // Get opacity from the selected layer (not just the first enabled one)
      // Find the selected layer config to get its default opacity
      const selectedLayerConfig = groupLayers.find(
        (lc) => lc.id === selectedLayerId
      )
      const selectedLayerState =
        selectedLayerId !== 'none' && dynamicLayerStates[selectedLayerId]
          ? dynamicLayerStates[selectedLayerId]
          : {
              enabled: true,
              opacity: selectedLayerConfig?.defaultOpacity || 30,
            }

      // Create a single layer with options for the dropdown
      const groupLabelKey: Record<string, string> = {
        'demographics-choropleth': 'demographicsLayer',
        'employment-choropleth': 'employmentLayer',
        'purchase-market-choropleth': 'purchaseMarketLayer',
        'rental-market-choropleth': 'rentalMarketLayer',
        'noise-choropleth': 'noiseLayer',
        'zoning-layers': 'zoningLayer',
        'historical-imagery': 'historicalImagery',
      }

      const groupTitleKeyMap: Record<string, string> = {
        'demographics-choropleth': 'demographics',
        'employment-choropleth': 'employment',
        'purchase-market-choropleth': 'purchaseMarket',
        'rental-market-choropleth': 'rentalMarket',
        'noise-choropleth': 'noiseAnalysis',
        'zoning-layers': 'zoning',
        'historical-imagery': 'historicalSatelliteImages',
      }

      const getLayerLabel = () => {
        const key = groupLabelKey[groupId]
        return key ? tUi(`analysisMap.groupLabels.${key}`) : groupId
      }

      const getGroupTitle = () => {
        const key = groupTitleKeyMap[groupId]
        return key ? tUi(`analysisMap.groupTitles.${key}`) : groupId
      }

      // Check if the selected layer config has opacity support
      const selectedLayerHasOpacity = selectedLayerConfig?.hasOpacity !== false

      // Map layer IDs to zoning types for disabled state check
      const layerIdToZoningType: Record<string, ZoningType> = {
        'zoning-general-only': 'general',
        'zoning-cantonal-only': 'cantonal',
        'zoning-local-only': 'local',
        'zoning-partial-only': 'partial',
      }

      const exclusiveLayer = {
        id: groupId,
        label: getLayerLabel(),
        enabled: true, // Radio groups are always "enabled" (one option is always selected)
        type: 'radio' as const,
        renderAs: 'select' as const, // Use dropdown for exclusive groups
        selectedValue: selectedLayerId,
        opacity: selectedLayerState.opacity,
        showOpacity: selectedLayerHasOpacity, // Only show if layer config allows opacity
        options: groupLayers.map((lc) => {
          // Check if this option should be disabled (for zoning type filter only)
          const isZoningTypeFilter = groupId === 'zoning-type-filter'
          const zoningType = layerIdToZoningType[lc.id]
          const isDisabled =
            isZoningTypeFilter &&
            zoningType &&
            disabledZoningTypes.includes(zoningType)

          const translationKey = LAYER_TRANSLATION_KEYS[lc.id]
          const label = translationKey
            ? tUi(`analysisMap.layers.${translationKey}`)
            : lc.name
          const description = translationKey
            ? tUi(`analysisMap.layers.${translationKey}Description`)
            : lc.description

          return {
            value: lc.id,
            label,
            description,
            disabled: isDisabled,
          }
        }),
      }

      layerGroups.push({
        id: groupId,
        title: getGroupTitle(),
        groupType: 'dynamic' as const,
        groupRenderType: 'exclusive-dropdown' as const,
        sectionName: groupTitle,
        icon: 'layers',
        defaultExpanded: true,
        showNoLayerOption: shouldShowNoLayerOption,
        layers: [exclusiveLayer],
      })
    })

    // Add independent layers as a multiple-checkbox group if any exist
    if (independentLayers.length > 0) {
      const layers = independentLayers.map((layerConfig) => {
        const layerState = dynamicLayerStates[layerConfig.id] || {
          enabled: layerConfig.defaultEnabled,
          opacity: layerConfig.defaultOpacity || 30,
        }

        return {
          id: layerConfig.id,
          label: LAYER_TRANSLATION_KEYS[layerConfig.id]
            ? tUi(
                `analysisMap.layers.${LAYER_TRANSLATION_KEYS[layerConfig.id]}`
              )
            : layerConfig.name,
          enabled: layerState.enabled,
          type: 'toggle' as const,
          opacity: layerState.opacity,
          showOpacity: layerConfig.hasOpacity || false,
        }
      })

      layerGroups.push({
        id: 'independent-layers',
        title: tUi('analysisMap.groupTitles.layers'),
        groupType: 'dynamic' as const,
        groupRenderType: 'multiple-checkbox' as const,
        sectionName: groupTitle,
        icon: 'layers',
        defaultExpanded: true,
        layers,
      })
    }

    return layerGroups
  }, [
    contextActiveSection,
    hasActiveSectionLayers,
    sectionData,
    dynamicLayerStates,
    sectionLayerConfigs,
    disabledZoningTypes,
    tUi,
  ])

  /**
   * Build dynamic layers data from section data and layer states
   */
  const getDynamicLayersData = () => {
    if (!contextActiveSection || !contextSectionData) {
      return []
    }

    const dynamicLayers: Array<{
      id: string
      type: 'markers' | 'heatmap' | 'polygon' | 'line'
      geoJson: any
      visible: boolean
      config?: {
        clustering?: boolean
        clusterMaxZoom?: number
        colors?: string[]
        opacity?: number
        hoveredId?: string | number | null
        selectedId?: string | number | null
        fillExpression?: any
        strokeColor?: string
        strokeWidth?: number
        lineOnly?: boolean
        lineDasharray?: number[]
      }
    }> = []

    // GeoJSON fallback for listings/transactions/building permits
    // when vector tile config is not available (e.g., public read-only mode)
    const hasVectorTiles = !!(contextSectionData as any)?.vectorTileConfig
      ?.tileUrl

    if (!hasVectorTiles) {
      // Parse geo_center which can be { lat, lon } object or "lon,lat" string
      const parseGeoCenter = (gc: any): [number, number] | null => {
        if (!gc) return null
        if (typeof gc === 'object' && gc.lat != null && gc.lon != null) {
          return [gc.lon, gc.lat]
        }
        if (typeof gc === 'string') {
          const parts = gc.split(',').map(Number)
          if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
            return [parts[0], parts[1]] // lon, lat
          }
        }
        return null
      }

      // Flatten item to GeoJSON properties (only serializable values)
      const toProperties = (item: any): Record<string, any> => {
        const props: Record<string, any> = {}
        for (const [key, val] of Object.entries(item)) {
          if (
            val == null ||
            typeof val === 'string' ||
            typeof val === 'number' ||
            typeof val === 'boolean'
          ) {
            props[key] = val
          }
        }
        if (!props.id) props.id = item.id || item._id
        return props
      }

      // Listings markers (purchase or rental)
      if (
        contextActiveSection === 'purchase_listings' ||
        contextActiveSection === 'rental_listings'
      ) {
        const listings = (contextSectionData as any)?.listings || []
        const features = listings
          .map((l: any) => {
            const coords = parseGeoCenter(l.geo_center)
            if (!coords) return null
            return {
              type: 'Feature' as const,
              geometry: { type: 'Point' as const, coordinates: coords },
              properties: toProperties(l),
            }
          })
          .filter(Boolean)

        if (features.length > 0) {
          const color =
            contextActiveSection === 'purchase_listings' ? '#E68600' : '#00A550'
          const layerState = dynamicLayerStates['listings-markers']
          dynamicLayers.push({
            id: 'listings-markers',
            type: 'markers',
            geoJson: { type: 'FeatureCollection', features },
            visible: layerState?.enabled ?? true,
            config: {
              clustering: true,
              clusterMaxZoom: 14,
              colors: [color],
              hoveredId: hoveredItemId,
              selectedId: selectedItemId,
            },
          })
        }
      }

      // Transactions markers
      if (contextActiveSection === 'transactions') {
        const transactions = (contextSectionData as any)?.transactions || []
        const features = transactions
          .map((t: any) => {
            const coords =
              parseGeoCenter(t.geo_center) || parseGeoCenter(t.land_geo_center)
            if (!coords) return null
            return {
              type: 'Feature' as const,
              geometry: { type: 'Point' as const, coordinates: coords },
              properties: toProperties(t),
            }
          })
          .filter(Boolean)

        if (features.length > 0) {
          const layerState = dynamicLayerStates['transaction-points']
          dynamicLayers.push({
            id: 'transaction-points',
            type: 'markers',
            geoJson: { type: 'FeatureCollection', features },
            visible: layerState?.enabled ?? true,
            config: {
              clustering: true,
              clusterMaxZoom: 14,
              colors: ['#3b82f6'],
              hoveredId: hoveredItemId,
              selectedId: selectedItemId,
            },
          })
        }
      }

      // Building permits markers
      if (contextActiveSection === 'building_permits') {
        const permits = (contextSectionData as any)?.building_permits || []
        const features = permits
          .map((p: any) => {
            const coords = parseGeoCenter(p.geo_center)
            if (!coords) return null
            return {
              type: 'Feature' as const,
              geometry: { type: 'Point' as const, coordinates: coords },
              properties: toProperties(p),
            }
          })
          .filter(Boolean)

        if (features.length > 0) {
          const layerState = dynamicLayerStates['building-permits-markers']
          dynamicLayers.push({
            id: 'building-permits-markers',
            type: 'markers',
            geoJson: { type: 'FeatureCollection', features },
            visible: layerState?.enabled ?? true,
            config: {
              clustering: true,
              clusterMaxZoom: 14,
              colors: ['#8b5cf6'],
              hoveredId: hoveredItemId,
              selectedId: selectedItemId,
            },
          })
        }
      }
    }

    // Handle amenities markers layer - create 9 separate layers with separate sources (one per category)
    if (contextActiveSection === 'amenities') {
      const amenityPOIs = (contextSectionData as any).amenities || []

      if (amenityPOIs.length > 0) {
        // Create 9 separate layers, each with its own filtered GeoJSON source
        const categories = [
          'shopping',
          'grocery',
          'culture',
          'dining',
          'education',
          'wellness',
          'healthcare',
          'transport',
          'services',
        ]

        categories.forEach((category) => {
          const layerId = `amenities-${category}`
          const layerConfig = sectionLayerConfigs.find(
            (config) => config.id === layerId
          )
          const layerState = dynamicLayerStates[layerId] || {
            enabled: layerConfig?.defaultEnabled !== false,
            opacity: layerConfig?.defaultOpacity || 30,
          }

          // Filter POIs by category and create separate GeoJSON for this category
          const categoryPOIs = amenityPOIs.filter(
            (poi: any) => poi.category === category
          )
          const categoryGeoJson = amenityPOIsToGeoJSON(categoryPOIs)

          dynamicLayers.push({
            id: layerId,
            type: 'markers',
            geoJson: categoryGeoJson, // Separate GeoJSON for each category
            visible: layerState.enabled,
            config: {
              clustering: layerConfig?.render?.clustering || true,
              clusterMaxZoom: layerConfig?.render?.clusterMaxZoom || 14,
              colors: layerConfig?.render?.colors || ['#808080'],
              opacity: layerState.opacity,
              hoveredId: hoveredItemId,
              selectedId: selectedItemId,
              // Category filter no longer needed since source is pre-filtered
            },
          })
        })
      }
    }

    // Handle Transport & Access section - 8 layers (3 stops + 3 isochrones + 2 infrastructure)
    if (contextActiveSection === 'transport') {
      const transportData = (contextSectionData as any)?.transport

      // 1. Transport stops (bus, tram, train) - 3 separate layers
      const publicTransportStops = transportData?.publicTransportStops || []

      if (publicTransportStops.length > 0) {
        // Create 3 separate stop layers with pre-filtered sources
        const stopTypes: Array<'bus' | 'tram' | 'train'> = [
          'bus',
          'tram',
          'train',
        ]

        // The map layers use lowercase 'bus'/'tram'/'train'; stopServesMode
        // takes the ATLAS mode vocabulary, so that mapping lives here at the
        // call site rather than in the predicate itself. Tram and Metro share
        // a layer, matching the "Tram & Metro" convention used elsewhere.
        const modesByStopType: Record<
          (typeof stopTypes)[number],
          TransitMode[]
        > = {
          bus: ['BUS'],
          tram: ['TRAM', 'METRO'],
          train: ['TRAIN'],
        }

        // Resolved up front: the dedup pass below needs to know which of a
        // multi-modal stop's layers are actually visible before it can pick
        // one to own that stop.
        const layerStateByType = stopTypes.reduce(
          (acc, stopType) => {
            const layerId = `transport-${stopType}-stops`
            const layerConfig = sectionLayerConfigs.find(
              (config) => config.id === layerId
            )
            acc[stopType] = dynamicLayerStates[layerId] || {
              enabled: layerConfig?.defaultEnabled !== false,
              opacity: layerConfig?.defaultOpacity || 30,
            }
            return acc
          },
          {} as Record<
            (typeof stopTypes)[number],
            { enabled: boolean; opacity: number }
          >
        )

        /*
          stopServesMode overlaps by design (a tram+bus interchange serves
          both) - that overlap is what fixes "toggle Bus Stops off and the
          interchange vanishes". Left alone though, a stop whose two serving
          layers are both visible at once would be drawn twice, once from
          each layer's own GeoJSON source, stacked on the same coordinate.
          Claim each stop for exactly one of its currently-visible layers
          (train, then tram, then bus) so it is never missing and never
          doubled; the pin's own colour (see useDynamicLayers.ts) still marks
          it an interchange regardless of which layer ends up drawing it.
        */
        const claimedStopIds = new Set<string>()
        const stopsByType: Record<(typeof stopTypes)[number], any[]> = {
          bus: [],
          tram: [],
          train: [],
        }

        stopTypes.forEach((stopType) => {
          const modes = modesByStopType[stopType]
          publicTransportStops.forEach((stop: any) => {
            if (!modes.some((mode) => stopServesMode(stop, mode))) return

            const stopId = String(
              stop.id ?? stop.featureId ?? `${stop.name}-${stop.distance}`
            )
            if (layerStateByType[stopType].enabled) {
              if (claimedStopIds.has(stopId)) return
              claimedStopIds.add(stopId)
            }
            stopsByType[stopType].push(stop)
          })
        })

        stopTypes.forEach((stopType) => {
          const layerId = `transport-${stopType}-stops`
          const layerConfig = sectionLayerConfigs.find(
            (config) => config.id === layerId
          )
          const layerState = layerStateByType[stopType]
          const stopsGeoJson = transportStopsToGeoJSON(
            stopsByType[stopType],
            stopType
          )

          dynamicLayers.push({
            id: layerId,
            type: 'markers',
            geoJson: stopsGeoJson,
            visible: layerState.enabled,
            config: {
              clustering: layerConfig?.render?.clustering || true,
              clusterMaxZoom: layerConfig?.render?.clusterMaxZoom || 14,
              colors: layerConfig?.render?.colors || ['#808080'],
              opacity: layerState.opacity,
              hoveredId: hoveredItemId,
              selectedId: selectedItemId,
            },
          })
        })
      }

      // 2. Travel time isochrones (walking, cycling, driving) - 3 mutually exclusive layers
      const isochrones = transportData?.isochrones

      if (isochrones) {
        const travelModes: Array<'walking' | 'cycling' | 'driving'> = [
          'walking',
          'cycling',
          'driving',
        ]

        travelModes.forEach((mode) => {
          const layerId = `transport-${mode}-isochrones`
          const layerConfig = sectionLayerConfigs.find(
            (config) => config.id === layerId
          )
          const layerState = dynamicLayerStates[layerId] || {
            enabled: layerConfig?.defaultEnabled !== false,
            opacity: layerConfig?.defaultOpacity || 30,
          }

          // Only add layer if it's enabled (mutual exclusivity handled by layer panel)
          if (layerState.enabled) {
            const isochroneGeoJson = isochronesToGeoJSON(isochrones, mode)

            dynamicLayers.push({
              id: layerId,
              type: 'polygon',
              geoJson: isochroneGeoJson,
              visible: true,
              config: {
                colors: layerConfig?.render?.colors || [
                  '#22c55e',
                  '#eab308',
                  '#f97316',
                  '#ef4444',
                ],
                opacity: layerState.opacity,
              },
            })
          }
        })
      }

      // 3. EV Charging stations
      const evStations =
        transportData?.activeMobilityMetrics?.evStationsNearby || []

      if (evStations.length > 0) {
        const layerId = 'transport-ev-charging'
        const layerConfig = sectionLayerConfigs.find(
          (config) => config.id === layerId
        )
        const layerState = dynamicLayerStates[layerId] || {
          enabled: layerConfig?.defaultEnabled !== false,
          opacity: layerConfig?.defaultOpacity || 30,
        }

        const evGeoJson = evStationsToGeoJSON(evStations)

        dynamicLayers.push({
          id: layerId,
          type: 'markers',
          geoJson: evGeoJson,
          visible: layerState.enabled,
          config: {
            clustering: layerConfig?.render?.clustering || true,
            clusterMaxZoom: layerConfig?.render?.clusterMaxZoom || 14,
            colors: layerConfig?.render?.colors || ['#8b5cf6'],
            opacity: layerState.opacity,
            hoveredId: hoveredItemId,
            selectedId: selectedItemId,
          },
        })
      }

      // 4. Highway access points
      const highwayAccess =
        transportData?.roadAccessMetrics?.allHighwayAccess || []

      if (highwayAccess.length > 0) {
        const layerId = 'transport-highway-access'
        const layerConfig = sectionLayerConfigs.find(
          (config) => config.id === layerId
        )
        const layerState = dynamicLayerStates[layerId] || {
          enabled: layerConfig?.defaultEnabled !== false,
          opacity: layerConfig?.defaultOpacity || 30,
        }

        const highwayGeoJson = highwayAccessToGeoJSON(highwayAccess)

        dynamicLayers.push({
          id: layerId,
          type: 'markers',
          geoJson: highwayGeoJson,
          visible: layerState.enabled,
          config: {
            clustering: layerConfig?.render?.clustering || true,
            clusterMaxZoom: layerConfig?.render?.clusterMaxZoom || 14,
            colors: layerConfig?.render?.colors || ['#10b981'],
            opacity: layerState.opacity,
            hoveredId: hoveredItemId,
            selectedId: selectedItemId,
          },
        })
      }
    }

    // Handle energy section - solar potential roof polygons
    if (contextActiveSection === 'energy') {
      const roofData = (contextSectionData as any).roofData

      if (roofData && roofData.features && roofData.features.length > 0) {
        const layerId = 'energy-solar-potential'
        const layerConfig = sectionLayerConfigs.find(
          (config) => config.id === layerId
        )
        const layerState = dynamicLayerStates[layerId] || {
          enabled: layerConfig?.defaultEnabled !== false,
          opacity: layerConfig?.defaultOpacity || 60,
        }

        const roofGeoJson = roofDataToGeoJSON(roofData)

        dynamicLayers.push({
          id: layerId,
          type: 'polygon',
          geoJson: roofGeoJson,
          visible: layerState.enabled,
          config: {
            colors: layerConfig?.render?.colors || [
              '#2DC5FF',
              '#FFFC09',
              '#FCAA00',
              '#FA5502',
              '#A80200',
            ],
            opacity: layerState.opacity,
            fillExpression: createSolarPotentialFillExpression(),
          },
        })
      }
    }

    // Handle demographics section - isochrone layer for travel time area
    if (contextActiveSection === 'demographics') {
      const demographicsData = (contextSectionData as any)?.demographics
      const isochrone = demographicsData?.isochrone

      if (isochrone && isochrone.features && isochrone.features.length > 0) {
        const layerId = 'demographics-isochrone'
        const layerConfig = sectionLayerConfigs.find(
          (config) => config.id === layerId
        )
        const layerState = dynamicLayerStates[layerId] || {
          enabled: layerConfig?.defaultEnabled !== false,
          opacity: layerConfig?.defaultOpacity || 30,
        }

        dynamicLayers.push({
          id: layerId,
          type: 'polygon',
          geoJson: isochrone,
          visible: layerState.enabled,
          config: {
            colors: layerConfig?.render?.colors || ['#00ADE6'],
            opacity: layerState.opacity,
            strokeColor: layerConfig?.render?.strokeColor || '#00ADE6',
            strokeWidth: layerConfig?.render?.strokeWidth || 2,
            lineOnly: true, // Render as dashed border only, no fill
            lineDasharray: [1, 1], // Dash pattern
          },
        })
      }
    }

    // Handle employment section - isochrone layer for travel time area
    if (contextActiveSection === 'employment') {
      const employmentData = (contextSectionData as any)?.employment
      const isochrone = employmentData?.isochrone

      if (isochrone && isochrone.features && isochrone.features.length > 0) {
        const layerId = 'employment-isochrone'
        const layerConfig = sectionLayerConfigs.find(
          (config) => config.id === layerId
        )
        const layerState = dynamicLayerStates[layerId] || {
          enabled: layerConfig?.defaultEnabled !== false,
          opacity: layerConfig?.defaultOpacity || 30,
        }

        dynamicLayers.push({
          id: layerId,
          type: 'polygon',
          geoJson: isochrone,
          visible: layerState.enabled,
          config: {
            colors: layerConfig?.render?.colors || ['#00ADE6'],
            opacity: layerState.opacity,
            strokeColor: layerConfig?.render?.strokeColor || '#00ADE6',
            strokeWidth: layerConfig?.render?.strokeWidth || 2,
            lineOnly: true, // Render as dashed border only, no fill
            lineDasharray: [1, 1], // Dash pattern
          },
        })
      }
    }

    return dynamicLayers
  }

  /**
   * Generate vector tile dynamic layers data from section vectorTileConfig.
   * Rich config with clustering, labels, tooltips, overlap popovers, and hover/select.
   */
  const getVectorTileDynamicLayersData = (): VectorTileDynamicLayerData[] => {
    if (!contextActiveSection || !contextSectionData) {
      return []
    }

    const vtConfig = (contextSectionData as any)?.vectorTileConfig
    if (!vtConfig?.tileUrl) {
      return []
    }

    // Only show vector tile layers for these sections
    const supportedSections = [
      'purchase_listings',
      'rental_listings',
      'transactions',
      'building_permits',
    ]
    if (!supportedSections.includes(contextActiveSection)) {
      return []
    }

    // Map section to the dynamic layer ID that controls visibility
    const sectionToLayerIdMap: Record<string, string> = {
      purchase_listings: 'listings-markers',
      rental_listings: 'listings-markers',
      transactions: 'transaction-points',
      building_permits: 'building-permits-markers',
    }

    const layerId = sectionToLayerIdMap[contextActiveSection]
    const layerState = dynamicLayerStates[layerId]
    const isVisible = layerState?.enabled ?? true

    // Per-section rich config
    const sectionConfigs: Record<
      string,
      Omit<
        VectorTileDynamicLayerData,
        'id' | 'tileUrl' | 'sourceLayer' | 'visible'
      >
    > = {
      purchase_listings: {
        pointStyle: {
          color: '#E68600',
          size: 6,
          strokeColor: '#ffffff',
          strokeWidth: 1,
        },
        clustering: {
          enabled: true,
          aggsSourceLayer: vtConfig.aggsSourceLayer || 'aggs',
          maxZoom: 13,
          clusterColor: '#E68600',
        },
        labelStyle: {
          enabled: true,
          field: 'price',
          backgroundColor: '#E68600',
          textColor: '#ffffff',
          fontSize: 11,
          minZoom: 11,
          pill: true,
        },
        tooltip: { enabled: true, fields: ['price'] },
        overlapPopover: { enabled: true },
        hoveredItemId,
        selectedItemId,
      },
      rental_listings: {
        pointStyle: {
          color: '#00A550',
          size: 6,
          strokeColor: '#ffffff',
          strokeWidth: 1,
        },
        clustering: {
          enabled: true,
          aggsSourceLayer: vtConfig.aggsSourceLayer || 'aggs',
          maxZoom: 13,
          clusterColor: '#00A550',
        },
        labelStyle: {
          enabled: true,
          field: 'price',
          backgroundColor: '#00A550',
          textColor: '#ffffff',
          fontSize: 11,
          minZoom: 11,
          pill: true,
        },
        tooltip: { enabled: true, fields: ['price'] },
        overlapPopover: { enabled: true },
        hoveredItemId,
        selectedItemId,
      },
      transactions: {
        pointStyle: {
          color: '#E05252',
          size: 6,
          strokeColor: '#ffffff',
          strokeWidth: 1,
        },
        clustering: {
          enabled: true,
          aggsSourceLayer: vtConfig.aggsSourceLayer || 'aggs',
          maxZoom: 12,
          clusterColor: '#E05252',
        },
        labelStyle: {
          enabled: true,
          field: 'transaction_total_price',
          backgroundColor: '#E05252',
          textColor: '#ffffff',
          fontSize: 11,
          minZoom: 11,
          pill: true,
        },
        tooltip: { enabled: true, fields: ['transaction_total_price'] },
        overlapPopover: { enabled: true },
        hoveredItemId,
        selectedItemId,
      },
      building_permits: {
        pointStyle: {
          color: '#805AD5',
          size: 6,
          strokeColor: '#ffffff',
          strokeWidth: 1,
        },
        clustering: {
          enabled: true,
          aggsSourceLayer: vtConfig.aggsSourceLayer || 'aggs',
          maxZoom: 12,
          clusterColor: '#805AD5',
        },
        labelStyle: {
          enabled: true,
          field: 'regbl_classification_general',
          backgroundColor: '#805AD5',
          textColor: '#ffffff',
          fontSize: 10,
          minZoom: 13,
          pill: true,
        },
        tooltip: { enabled: true, fields: ['description'] },
        overlapPopover: {
          enabled: true,
          title: (count: number) => `${count} permits at this location`,
          renderItem: (properties: Record<string, any>) => {
            const type =
              properties.regbl_classification_general ||
              properties.work_type_official ||
              ''
            const date = properties.inquiry_start_date
              ? new Date(properties.inquiry_start_date).toLocaleDateString(
                  'fr-CH'
                )
              : ''
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#805AD5',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#333',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {type}
                </span>
                {date && (
                  <span
                    style={{
                      fontSize: '10px',
                      color: '#999',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {date}
                  </span>
                )}
              </div>
            )
          },
        },
        hoveredItemId,
        selectedItemId,
      },
    }

    const config = sectionConfigs[contextActiveSection]
    if (!config) return []

    return [
      {
        id: `vt-${contextActiveSection}`,
        tileUrl: vtConfig.tileUrl,
        sourceLayer: vtConfig.sourceLayer,
        visible: isVisible,
        ...config,
      },
    ]
  }

  /**
   * Handle click on a vector tile layer feature.
   * Vector tiles only carry properties from the tile, so we use the ID
   * to select the item and trigger the onItemClick callback.
   */
  const handleVectorTileLayerClick = useCallback(
    (properties: any, _layerId: string) => {
      const itemId = properties?.id
      if (!itemId) return

      if (setSelectedItemId) {
        setSelectedItemId(itemId)
      }

      if (onItemClick) {
        onItemClick({ id: itemId, ...properties })
      }
    },
    [setSelectedItemId, onItemClick]
  )

  /**
   * Callbacks for VT tooltip/popover state management
   */
  const handleVTTooltipChange = useCallback((data: VTTooltipData | null) => {
    setVtTooltipData(data)
  }, [])

  const handleVTPopoverChange = useCallback((data: VTPopoverData | null) => {
    setVtPopoverData(data)
  }, [])

  /**
   * Render VT tooltip based on active section
   */
  const renderVTTooltip = () => {
    if (!vtTooltipData || vtPopoverData) return null

    const { feature, x, y, overlapCount } = vtTooltipData
    const properties = feature?.properties || {}

    const overlapHint = overlapCount ? (
      <TooltipOverlapHint>
        {tUi('map.vectorTile.overlapCount', { count: overlapCount })}
      </TooltipOverlapHint>
    ) : null

    let content = null
    if (
      contextActiveSection === 'purchase_listings' ||
      contextActiveSection === 'rental_listings'
    ) {
      content = <ListingMapTooltip properties={properties} />
    } else if (contextActiveSection === 'transactions') {
      content = <TransactionMapTooltip properties={properties} />
    } else if (contextActiveSection === 'building_permits') {
      content = <BuildingPermitMapTooltip properties={properties} />
    }

    if (!content) return null

    return (
      <TooltipContainer $x={x} $y={y}>
        {content}
        {overlapHint}
      </TooltipContainer>
    )
  }

  /**
   * Render VT popover for overlapping features
   */
  const renderVTPopover = () => {
    if (!vtPopoverData) return null

    const { features, x, y } = vtPopoverData

    // Get the active layer config for custom renderItem/title
    const vtLayers = getVectorTileDynamicLayersData()
    const activeLayer = vtLayers[0]
    const popoverConfig = activeLayer?.overlapPopover

    const popoverCopy = getVectorTilePopoverCopy(
      contextActiveSection,
      features.length,
      (key, options) => tUi(key, options)
    )
    const title = popoverConfig?.title
      ? popoverConfig.title(features.length)
      : popoverCopy.title

    return (
      <PopoverContainer $x={x} $y={y}>
        <PopoverHeader>
          <span>{title}</span>
          <PopoverCloseButton
            aria-label={tUi('detailPanel.close')}
            onClick={() => setVtPopoverData(null)}
          >
            ×
          </PopoverCloseButton>
        </PopoverHeader>
        <PopoverList>
          {features.map((feature: any, index: number) => {
            const properties = feature.properties || {}

            // Use custom renderItem if provided by the layer config
            let itemContent = null
            if (popoverConfig?.renderItem) {
              itemContent = popoverConfig.renderItem(properties, index)
            } else if (
              contextActiveSection === 'purchase_listings' ||
              contextActiveSection === 'rental_listings'
            ) {
              itemContent = <ListingMapTooltip properties={properties} />
            } else if (contextActiveSection === 'transactions') {
              itemContent = <TransactionMapTooltip properties={properties} />
            }

            return (
              <PopoverItem
                key={properties?.id ?? feature.id ?? index}
                onClick={() => {
                  handleVectorTileLayerClick(properties, '')
                  setVtPopoverData(null)
                }}
              >
                {itemContent || <span>{properties?.id ?? feature.id}</span>}
              </PopoverItem>
            )
          })}
        </PopoverList>
        {features.length > 1 && (
          <PopoverScrollHint>
            <Icon name="chevron-down" size="small" />
            <span>{popoverCopy.scrollHint}</span>
          </PopoverScrollHint>
        )}
      </PopoverContainer>
    )
  }

  /**
   * Handle toggle for both base layers and dynamic layers
   */
  const handleAllLayersToggle = (layerId: string, enabled: boolean) => {
    // Check if this is a dynamic layer
    const isDynamicLayer = sectionLayerConfigs.some(
      (config) => config.id === layerId
    )

    // Also check if it's a dynamically created layer
    const isRestrictionLayer = layerId.startsWith('restrictions-')
    const isHazardLayer = layerId.startsWith('hazards-')

    if (isDynamicLayer || isRestrictionLayer || isHazardLayer) {
      toggleDynamicLayer(layerId, enabled)
    } else {
      // Base layer toggle
      handleLayerToggle(layerId, enabled)
    }
  }

  // Handle map style restoration from persistence
  const handleMapStyleRestore = useCallback((mapStyleUrl: string) => {
    // If this is right after a user-initiated change, ignore the restoration
    if (userInitiatedStyleChangeRef.current) {
      userInitiatedStyleChangeRef.current = false // Reset the flag
      return
    }

    // Convert the style URL back to style ID for the dropdown
    const styleId = getMapStyleIdFromUrl(mapStyleUrl)
    setCurrentMapStyle(styleId)
  }, [])

  const handleLayerOpacityChange = (layerId: string, opacity: number) => {
    // Check if this is an exclusive group (radio) - the layerId might be the group ID
    const exclusiveGroupLayers = sectionLayerConfigs.filter(
      (config) => config.exclusiveGroup === layerId
    )

    if (exclusiveGroupLayers.length > 0) {
      // This is an exclusive group - apply opacity to ALL layers in the group
      // This ensures that when switching between options, the opacity is preserved
      exclusiveGroupLayers.forEach((layerConfig) => {
        setLayerOpacity(layerConfig.id, opacity)
      })
    } else {
      // Regular layer - set opacity directly
      setLayerOpacity(layerId, opacity)
    }
  }

  const handleLayerRadioSelect = (groupId: string, selectedLayerId: string) => {
    // Map of zoning type filter layer IDs to zoning types
    const zoningTypeMap: Record<string, 'all' | ZoningType> = {
      'zoning-all': 'all',
      'zoning-general-only': 'general',
      'zoning-cantonal-only': 'cantonal',
      'zoning-local-only': 'local',
      'zoning-partial-only': 'partial',
    }

    // Detect zoning type filter changes and update context
    if (groupId === 'zoning-type-filter' && selectedLayerId in zoningTypeMap) {
      const zoningType = zoningTypeMap[selectedLayerId]
      setSelectedZoningType(zoningType)
      // Fall through to existing layer toggle logic
    }

    // Find all layers in this exclusive group
    const exclusiveGroupLayers = sectionLayerConfigs.filter(
      (config) => config.exclusiveGroup === groupId
    )

    // If "No layer" is selected (value = 'none'), disable all layers in the group
    if (selectedLayerId === 'none') {
      exclusiveGroupLayers.forEach((layerConfig) => {
        toggleDynamicLayer(layerConfig.id, false)
      })
      return
    }

    // Otherwise, disable all layers in the group except the selected one
    exclusiveGroupLayers.forEach((layerConfig) => {
      const shouldEnable = layerConfig.id === selectedLayerId
      toggleDynamicLayer(layerConfig.id, shouldEnable)
    })
  }

  /**
   * Render custom controls for specific layers
   * Currently supports sunlight study layers
   */
  const renderCustomControls = useCallback(
    (selectedLayerId: string) => {
      // Sunlight study layers
      if (
        selectedLayerId === 'sunlight-shadow-simulation' ||
        selectedLayerId === 'sunlight-hours'
      ) {
        const currentDate =
          contextSectionData?.sunlightConfig?.date || new Date()

        return (
          <SunlightLayerControls
            selectedLayerId={selectedLayerId}
            currentDate={currentDate}
            dateKey={currentDate.getTime().toString()}
            coordinates={coordinates}
            onDateChange={(newDate) => {
              setSectionData((prev: any) => ({
                ...prev,
                sunlightConfig: { date: newDate },
              }))
            }}
          />
        )
      }

      return null
    },
    [contextSectionData, setSectionData]
  )

  return (
    <MapContainer ref={mapContainerRef} className={className}>
      {children ? (
        children
      ) : (
        <>
          {/* Fullscreen Button */}
          <FullscreenButton
            onClick={toggleFullscreen}
            $isFullscreen={isFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            <Icon name={isFullscreen ? 'close' : 'maximize'} size="small" />
          </FullscreenButton>

          {/* Hide map (collapse pane) — desktop only, hidden while browser-fullscreen */}
          {onCollapseMap && !isFullscreen && (
            <CollapseButton
              onClick={onCollapseMap}
              title={tUi('analysisMap.controls.hideMap')}
              aria-label={tUi('analysisMap.controls.hideMap')}
            >
              <Icon name="panel-right-close" size="small" />
            </CollapseButton>
          )}

          <MapComponent
            isPublic={isPublic}
            persistenceContext={`analysis-${analysisType}`}
            center={coordinates}
            bbox={bbox}
            maxBbox={publicMaxBbox}
            zoom={18}
            autoFitBounds={autoFitBounds}
            bboxTransitionDuration={1000}
            height={mapHeight}
            mapStyle={getMapStyleUrl(currentMapStyle)}
            showControls={defaultUiConfig.showMapControls}
            showNavigation={defaultUiConfig.showZoomControls}
            enableDrawing={defaultUiConfig.showDrawingTools}
            enableMeasurement={true} // Enable measurement tools for drawing functionality
            initialDrawingData={initialDrawingData}
            onDrawingComplete={onDrawingComplete}
            onDrawingUpdate={onDrawingUpdate}
            onDrawingDelete={onDrawingDelete}
            showDimensionControl={defaultUiConfig.show3DButton}
            showBuiltInPrintControl={defaultUiConfig.showPrintButton}
            showPointCloudControl={showPointCloudControl}
            pointCloudActive={pointCloudButtonActive}
            onTogglePointCloud={
              controlled
                ? onPointCloudButtonClick
                : () => {
                    const next = !pointCloudActive
                    setPointCloudActive(next)
                    onPointCloudToggle?.(next)
                    // Reflect in the URL: ON → ?pointcloud=1, OFF → drop the param (default-off).
                    updateQuery({ pointcloud: next ? '1' : undefined })
                  }
            }
            layers={defaultMapLayers}
            onLayerToggle={handleLayerToggle}
            baseLayerStates={layerStates}
            onBaseLayerStatesRestore={setLayerStates}
            onMapStyleRestore={handleMapStyleRestore}
            showFlyToPosition={true}
            propertyCoordinates={coordinates}
            flyToPosition={flyToPosition}
            layersPanelOpen={layersPanelOpen}
            setLayersPanelOpen={setLayersPanelOpen}
            legendPanelOpen={legendPanelOpen}
            setLegendPanelOpen={setLegendPanelOpen}
            onChangeMapStyle={handleChangeMapStyle}
            mapStylePanelOpen={currentMapStyle.includes('satellite')}
            geoJson={geoJson}
            distanceLimitValue={
              distanceLimitValue ??
              (contextSectionData as any)?.distanceLimitValue
            }
            buildingGeoJson={buildingGeoJson}
            buildingHeight={buildingHeight}
            buildingDisplayHeight={buildingDisplayHeight}
            buildingClassification={buildingClassification}
            buildingProtectedRank={buildingProtectedRank}
            dynamicLayersData={getDynamicLayersData()}
            onDynamicLayerClick={handleDynamicLayerClick}
            vectorTileDynamicLayersData={getVectorTileDynamicLayersData()}
            onVectorTileLayerClick={handleVectorTileLayerClick}
            onVTTooltipChange={handleVTTooltipChange}
            onVTPopoverChange={handleVTPopoverChange}
            mapRestrictions={{
              minZoom: 8,
              maxZoom: 20,
              scrollZoom: true,
              doubleClickZoom: true,
              dragRotate: true,
              keyboard: true,
            }}
            setSelectedItemId={setSelectedItemId}
            propertyId={propertyId}
            excludeBuildingIds={excludeBuildingIds}
            activeSection={contextActiveSection}
            sectionData={contextSectionData}
            demographicsLayerStates={dynamicLayerStates}
            employmentLayerStates={dynamicLayerStates}
            selectedZoningType={selectedZoningType}
            disabledZoningTypes={disabledZoningTypes}
            isSatelliteZoning={isSatelliteZoning}
            historicalImageryLayerState={(() => {
              // Find which historical imagery layer is enabled
              const enabledYear = Object.keys(dynamicLayerStates).find(
                (key) =>
                  key.startsWith('historical-imagery-') &&
                  dynamicLayerStates[key]?.enabled
              )
              if (enabledYear) {
                // Extract year from layer ID (e.g., 'historical-imagery-2024' -> '2024')
                const year = enabledYear.replace('historical-imagery-', '')
                return {
                  selectedYear: year,
                  opacity: dynamicLayerStates[enabledYear]?.opacity || 80,
                }
              }
              return { selectedYear: 'disabled', opacity: 80 }
            })()}
          >
            {onMapState ? <MapStateForwarder onMapState={onMapState} /> : null}
          </MapComponent>

          {/* VT tooltip/popover overlays */}
          {renderVTPopover()}
          {renderVTTooltip()}

          {/* Analysis-specific Map Panels (Layers and Legend) */}
          <MapPanels
            showLayerSelector={defaultUiConfig.showLayersButton}
            showBuiltInLayersPanel={true}
            layersPanelOpen={layersPanelOpen}
            setLayersPanelOpen={setLayersPanelOpen}
            mapLayers={defaultMapLayers}
            onLayerToggle={handleAllLayersToggle}
            onLayerSelect={handleMapStyleChange}
            onLayerOpacityChange={handleLayerOpacityChange}
            currentMapStyle={currentMapStyle}
            selectedThematicLayer={selectedThematicLayer}
            setLegendPanelOpen={setLegendPanelOpen}
            dynamicLayerGroups={dynamicLayerGroups}
            renderCustomControls={renderCustomControls}
            showLegend={defaultUiConfig.showLegendButton}
            showBuiltInLegendPanel={true}
            legendPanelOpen={legendPanelOpen}
            legendConfigs={legendConfigs}
          />

          {overlayControls}

          {/* Resize handle for split screen layouts */}
          <div
            style={{
              position: 'absolute',
              left: '-4px',
              top: 0,
              bottom: 0,
              width: '8px',
              cursor: 'col-resize',
              background: 'transparent',
              zIndex: 1001,
              transition: 'all 0.2s ease',
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              handleResizeStart()
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#00ade6'
              e.currentTarget.style.opacity = '0.15'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.opacity = '1'
            }}
          />
        </>
      )}
    </MapContainer>
  )
}

/**
 * AnalysisMap component with MapSectionProvider wrapper
 *
 * This is the main export that provides the section context to the internal component.
 */
export const AnalysisMap: React.FC<AnalysisMapProps> = (props) => {
  return (
    <MapSectionProvider
      initialSection={props.activeSection || null}
      initialData={props.sectionData || null}
    >
      <AnalysisMapInternal {...props} />
    </MapSectionProvider>
  )
}
